import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import 'jspdf-autotable';
import { CSVLink } from 'react-csv';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { Table, Select, Button, Modal, Form, Input, notification, Tabs, Row, Col, Space } from 'antd';
import { DeleteOutlined, ReloadOutlined, ClearOutlined  } from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import InvoiceModal from './InvoiceModal';
import PaymentDetails from './PaymentDetails';
import AddExpense from './AddExpense';
import ExpenseReport from './ExpenseReport';
import { useTranslation } from 'react-i18next';
import './Accounting.css';
import moment from 'moment';

const { Option } = Select;
const { Search } = Input;

const formatDate = (dateString) => {
  return moment(dateString).format('DD MMM YYYY');
};

const formatNumber = (number) => {
  if (isNaN(number)) return '0.00';
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

const Accounting = () => {
  const { t, i18n } = useTranslation();
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [filters, setFilters] = useState({
  dateRange: {
    start: moment().startOf('month').format('YYYY-MM-DD'),
    end: moment().endOf('month').format('YYYY-MM-DD')
  },
  paymentMethod: '',
  searchText: '',
});
  const [bankDetails, setBankDetails] = useState([]);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [form] = Form.useForm(); 

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch bank details
  const fetchBankDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/bank-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBankDetails(response.data);
    } catch (error) {
      console.error('[fetchBankDetails] Error fetching bank details:', error);
      notification.error({
        message: t('error'),
        description: 'Failed to fetch bank details'
      });
    }
  }, [API_URL, t]);

  const fetchRegistrations = useCallback(async () => {
    try {
        const token = localStorage.getItem('token');

        // Fetch all registrations (with remaining_balance included)
        const response = await axios.get(`${API_URL}/registrations`, {
            headers: { Authorization: `Bearer ${token}` },
        });

        // Directly use the API response
        setRegistrations(response.data);
        setFilteredRegistrations(response.data);

    } catch (error) {
        console.error('Error fetching registrations:', error);
    }
}, [API_URL]);

// Fetch registrations and bank details when the component mounts
useEffect(() => {
    fetchRegistrations();
    fetchBankDetails();
}, [fetchRegistrations, fetchBankDetails]);

const handleDelete = async (id) => {
  Modal.confirm({
    title: t('confirmDelete'),
    content: t('areYouSureDelete'),
    okText: t('yes'),
    okType: 'danger',
    cancelText: t('cancel'),
    onOk: async () => {
      try {
        const token = localStorage.getItem('token');

        await axios.delete(`${API_URL}/registrations/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        notification.success({
          message: t('deletedSuccess'),
          description: t('recordDeleted'),
        });

        fetchRegistrations();
      } catch (error) {
        console.error('Error deleting registration:', error);
        if (error.response?.data?.message === 'Cannot delete: Payments exist for this registration.') {
          notification.error({
            message: t('deleteError'),
            description: t('cannotDeleteDueToPayments'),
          });
        } else {
          notification.error({
            message: t('deleteError'),
            description: t('couldNotDelete'),
          });
        }
      }
    },
  });
};

  // Enhanced filter function
  const applyFilters = useCallback(() => {
    let filtered = registrations;

    // Date range filter
    if (filters.dateRange?.start && filters.dateRange?.end) {
  const { start, end } = filters.dateRange;
  const startDate = moment(start);
  const endDate = moment(end);

  filtered = filtered.filter(registration => {
    const regDate = moment(registration.date);
    return regDate.isBetween(startDate, endDate, 'day', '[]');
  });
}

    // Payment method filter
    if (filters.paymentMethod) {
      filtered = filtered.filter(registration => registration.payment_method === filters.paymentMethod);
    }

    // Search text filter (invoice number or student name)
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(registration => 
        registration.invoiceno.toLowerCase().includes(searchLower) ||
        registration.student_name.toLowerCase().includes(searchLower) ||
        registration.class_code.toLowerCase().includes(searchLower)
      );
    }

    setFilteredRegistrations(filtered);
  }, [registrations, filters]);

  // Apply filters whenever filters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
  setFilters({
    searchText: '',
    paymentMethod: '',
    dateRange: {
      start: moment().startOf('month').format('YYYY-MM-DD'),
      end: moment().endOf('month').format('YYYY-MM-DD'),
    },
  });
};


  const handlePaymentMethodChange = (value) => {
  setSelectedPaymentMethod(value);
  
  // Clear bank account selection if not bank transfer
  if (value !== 'bank_transfer') {
    form.setFieldsValue({ bank_account_id: undefined });
  } else {
    // Auto-select default bank for bank transfers
    if (bankDetails.length > 0) {
      const defaultBank = bankDetails.find(bank => bank.is_default) || bankDetails[0];
      form.setFieldsValue({ bank_account_id: defaultBank.id });
    }
  }
};

 const handleModalOk = async () => {
  try {
    const values = form.getFieldsValue();

    const paymentAmount = parseFloat(values.payment_amount);
    const remainingAmount = selectedRegistration.final_price - paymentAmount;

    const paymentData = {
      invoiceno: selectedRegistration.invoiceno,
      student_id: selectedRegistration.student_id,
      pay_amount: paymentAmount,
      remaining_balance: Math.round(remainingAmount),
      payment_method: values.payment_method,
      memo: values.memo,
    };

    // Only include bank_account_id for bank transfers
    if (values.payment_method === 'bank_transfer') {
      if (!values.bank_account_id) {
        notification.error({
          message: t('error'),
          description: t('Please select a bank account for bank transfer'),
        });
        return;
      }
      paymentData.bank_account_id = values.bank_account_id;
    }

    const token = localStorage.getItem('token');

    await axios.post(`${API_URL}/payment`, paymentData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    notification.success({
      message: t('paymentConfirmed'),
      description: t('Payment has been confirmed!'),
    });

    setIsPaymentModalOpen(false);
    setSelectedPaymentMethod('');
    form.resetFields();
    fetchRegistrations();
  } catch (error) {
    console.error('Error in handleModalOk:', error);

    if (error.response) {
      console.error('API Error Response:', error.response.data);
    }

    notification.error({
      message: t('error'),
      description: t('payment_log_error'),
    });
  }
};

 const openPaymentModal = (registration) => {
  setSelectedRegistration(registration);
  setSelectedPaymentMethod(''); // Reset payment method
  
  // Clear form when opening
  form.resetFields();
  
  setIsPaymentModalOpen(true);
};
  
  const openInvoiceModal = (transaction) => {
    setSelectedTransaction(transaction);
    setIsInvoiceModalOpen(true);
  };
  
  const handleInvoiceModalClose = () => {
    setIsInvoiceModalOpen(false); // Close the modal
    setSelectedTransaction(null); // Clear the selected transaction data
  };
  
  const calculateTotals = () => {
    return filteredRegistrations.reduce(
      (totals, registration) => {
        totals.price += parseFloat(registration.price) || 0;
        totals.discount += parseFloat(registration.discount) || 0;
        totals.vat += parseFloat(registration.vat) || 0;
        totals.finalPrice += parseFloat(registration.final_price) || 0;
        return totals;
      },
      { price: 0, discount: 0, vat: 0, finalPrice: 0 }
    );
  };

  const totals = calculateTotals();
  const csvData = [
    ...filteredRegistrations.map(reg => ({
      'INVOICE NO.': reg.invoiceno.replace('INV', ''),
      'DATE': moment(reg.date).format('DD MMM YYYY'),
      'STUDENT': reg.student_name,
      'COURSE': reg.class_code,
      'COURSE PRICE': reg.price,
      'INVENTORY PRICE': reg.inventory_price || 0,
      'DISCOUNT': reg.discount || 0,
      'VAT': reg.vat || 0,
      'TOTAL': reg.final_price,
      'REMAINING': reg.remaining_balance > 0 ? `(${reg.remaining_balance})` : 0
    })),
    {
      'INVOICE NO.': 'TOTAL',
      'DATE': '',
      'STUDENT': '',
      'COURSE': '',
      'COURSE PRICE': totals.price,
      'INVENTORY PRICE': filteredRegistrations.reduce((sum, reg) => sum + (parseFloat(reg.inventory_price) || 0), 0),
      'DISCOUNT': totals.discount,
      'VAT': totals.vat,
      'TOTAL': totals.finalPrice,
      'REMAINING': filteredRegistrations.reduce((sum, reg) => sum + (parseFloat(reg.remaining_balance) || 0), 0)
    }
  ];

  const columns = [
    {
      title: t('invoiceNo'),
      dataIndex: 'invoiceno',
      key: 'invoiceno',
      render: (text, record) => (
        <Button type="link" onClick={() => openInvoiceModal(record)}>
          {text.replace('INV', '')}
        </Button>
      ),
    },
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      render: formatDate,
    },
    {
      title: t('student'),
      dataIndex: 'student_name',
      key: 'student_name',
    },
    {
      title: t('course'),
      dataIndex: 'class_code',
      key: 'class_code',
    },
    {
      title: t('course price'),
      dataIndex: 'price',
      key: 'price',
      render: formatNumber,
    },
    {
      title: t('inventory price'),
      dataIndex: 'inventory_price',
      key: 'inventory_price',
      render: (text, record) => formatNumber(record.inventory_price, i18n.language),
    },
    {
      title: t('discount'),
      dataIndex: 'discount',
      key: 'discount',
      render: formatNumber,
    },
    {
      title: t('VAT'),
      dataIndex: 'vat',
      key: 'vat',
      render: formatNumber,
    },
    {
      title: t('total'),
      dataIndex: 'final_price',
      key: 'final_price',
      align: 'right',
      render: (value) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
          {formatNumber(value)}
        </span>
      ),
    },
    {
      title: t('remaining'),
      dataIndex: 'remaining_balance',
      key: 'remaining_balance',
      render: (balance) =>
        balance > 0 ? (
          <span style={{ fontWeight: 'bold', color: 'red' }}>({formatNumber(Math.abs(balance))})</span> // Wrap the balance in parentheses
        ) : (
          '0'
        ),
    },
    {
      title: t('pay'),
      key: 'pay',
      render: (_, record) => {
        const isDisabled = record.remaining_balance <= 0;
        return (
          <Button 
            onClick={() => openPaymentModal(record)} 
            icon={<FontAwesomeIcon icon={faDollarSign} />}
            disabled={isDisabled}
            style={{ opacity: isDisabled ? 0.5 : 1 }}
          />
        );
      },
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Button 
          type="primary" 
          danger  
          icon={<DeleteOutlined />} 
          onClick={() => handleDelete(record.id)} 
        />
      ),
    }     
  ];

  return (
    <div className="accounting-container">
      <Sidebar />
      <Header />
      <h1>{t('accounting')}</h1>

      {/* Ant Design Tabs with `items` prop */}
      <Tabs
        defaultActiveKey="1"
        items={[
          {
            key: "1",
            label: t('INVOICES'),
            children: (
              <>
                {/* Enhanced Filter Panel */}
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                      <Search
                        placeholder={t('Search by invoice, student name, or course')}
                        value={filters.searchText}
                        onChange={(e) => handleFilterChange('searchText', e.target.value)}
                        allowClear
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <div                      
                        style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}
                      >
                        <div>

                          <label htmlFor="start-date" style={{ fontSize: '13px' }}>
                            {t('From')}:
                          </label>
                          <input

                            type="date"
                            value={filters.dateRange.start}
                            onChange={(e) =>
                              handleFilterChange('dateRange', {
                                ...filters.dateRange,
                                start: e.target.value
                              })
                            }           
                            style={{
                              padding: '8px', 
                              borderRadius: '4px', 
                              border: '1px solid #d9d9d9',
                              height: '32px',
                              flex: 1
                            }}
                          />
                             {filters.dateRange.start && (
                            <div style={{ fontSize: '12px', color: '#555', textAlign: 'center' }}>
                              {moment(filters.dateRange.start).format('DD MMM YYYY')}
                            </div>
                          )}
                        </div>
                        <div>
                          <span>To: </span>
                          <input

                            type="date"
                            value={filters.dateRange.end}
                            onChange={(e) =>
                              handleFilterChange('dateRange', {
                                ...filters.dateRange,
                                end: e.target.value
                              })
                            }
                            style={{
                              padding: '8px', 
                              borderRadius: '4px', 
                              border: '1px solid #d9d9d9',
                              height: '32px',
                              flex: 1
                            }}
                          />
                          
                           {filters.dateRange.end && (
                            <div style={{ fontSize: '12px', color: '#555', textAlign: 'center' }}>
                              {moment(filters.dateRange.end).format('DD MMM YYYY')}
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                        <Col xs={24} sm={12} md={8}>
                         <Space>
                          <Button onClick={clearFilters} icon={<ClearOutlined />}>
                            {t('Clear Filter')}
                          </Button>
                          <Button onClick={fetchRegistrations} icon={<ReloadOutlined />}  style={{ marginRight: '10px'}}>
                            {t('refresh')}
                          </Button>
                        <CSVLink
                          data={csvData}
                          filename={`registrations_${moment().format('YYYY-MM-DD')}.csv`}
                          target="_blank"
                          headers={[
                            { label: 'INVOICE NO.', key: 'INVOICE NO.' },
                            { label: 'DATE', key: 'DATE' },
                            { label: 'STUDENT', key: 'STUDENT' },
                            { label: 'COURSE', key: 'COURSE' },
                            { label: 'COURSE PRICE', key: 'COURSE PRICE' },
                            { label: 'INVENTORY PRICE', key: 'INVENTORY PRICE' },
                            { label: 'DISCOUNT', key: 'DISCOUNT' },
                            { label: 'VAT', key: 'VAT' },
                            { label: 'TOTAL', key: 'TOTAL' },
                            { label: 'REMAINING', key: 'REMAINING' }
                          ]}
                        >
                          <Button icon={<FontAwesomeIcon icon={faFileExcel} />}>
                            {t('exportCSV')}
                          </Button>
                        </CSVLink>
                        </Space>
                      </Col>
                  </Row>
                  <Row style={{ marginTop: 16 }}>
                    <Col span={24}>
                      <Space>
                        <span style={{ fontSize: 14, color: '#666' }}>
                          {t('Showing')} {filteredRegistrations.length} of {registrations.length} {t('records')}
                        </span>
  
                      </Space>
                    </Col>
                  </Row>

                <Table
                  dataSource={filteredRegistrations}
                  columns={columns}
                  scroll={{ x: true }}
                  pagination={{
                    pageSize: 50,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  }}
                  rowKey="invoiceno"
                />
              </>
            ),
          },
          {
            key: "2",
            label: t('PAYMENTS'),
            children: <PaymentDetails />,
          },
          {
            key: "3",
            label: t('Add Expense'),
            children: <AddExpense />,
          },
          {
            key: "4",
            label: t('Expense Report'),
            children: <ExpenseReport />,
          },
        ]}
      />

      {/* Invoice Modal */}
      {selectedTransaction && (
        <InvoiceModal
          visible={isInvoiceModalOpen}
          onClose={handleInvoiceModalClose}
          registration={selectedTransaction}
          API_URL={API_URL}
        />
      )}

      {/* Enhanced Payment Modal */}
      <Modal
        title={t('makePayment')}
        open={isPaymentModalOpen}
        onOk={handleModalOk}
        onCancel={() => {
          setIsPaymentModalOpen(false);
          setSelectedPaymentMethod(''); // Reset payment method
          form.resetFields();
        }}
        width={600}
      >
        {selectedRegistration && (
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f6f6f6', borderRadius: 6 }}>
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <strong>{t('Invoice')}:</strong> {selectedRegistration.invoiceno}
              </Col>
              <Col span={12}>
                <strong>{t('Student')}:</strong> {selectedRegistration.student_name}
              </Col>
              <Col span={12}>
                <strong>{t('Total Amount')}:</strong> ฿{formatNumber(selectedRegistration.final_price)}
              </Col>
              <Col span={12}>
                <strong style={{ color: 'red' }}>{t('Remaining')}:</strong> ฿{formatNumber(selectedRegistration.remaining_balance)}
              </Col>
            </Row>
          </div>
        )}
        
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="payment_amount" 
                label={t('paymentAmount')} 
                rules={[
                  { required: true, message: t('Please enter payment amount') },
                  { 
                    validator: (_, value) => {
                      if (value && parseFloat(value) > selectedRegistration?.remaining_balance) {
                        return Promise.reject(new Error(t('Payment amount cannot exceed remaining balance')));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input 
                  placeholder={`Max: ${selectedRegistration?.remaining_balance || 0}`}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item 
                name="payment_method" 
                label={t('paymentMethod')} 
                rules={[{ required: true, message: t('Please select payment method') }]}
              >
                <Select 
                  placeholder={t('Select payment method')}
                  onChange={handlePaymentMethodChange}
                >
                  <Option value="cash">{t('Cash')}</Option>
                  <Option value="credit_card">{t('Credit Card')}</Option>
                  <Option value="bank_transfer">{t('Bank Transfer')}</Option>
                  <Option value="online_payment">{t('Online Payment')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          {/* Conditionally render bank account selection only for bank transfers */}
          {selectedPaymentMethod === 'bank_transfer' && (
            <Form.Item 
              name="bank_account_id" 
              label={t('Receiving Bank Account')} 
              rules={[{ required: true, message: t('Please select bank account') }]}
            >
              <Select 
                placeholder={t('Select bank account')}
                loading={bankDetails.length === 0}
              >
                {bankDetails.map(bank => (
                  <Option key={bank.id} value={bank.id}>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>
                        {bank.bank_name} - {bank.account_number} ({bank.account_name})
                        {bank.is_default && <span style={{ color: '#52c41a', fontSize: '12px' }}> (Default)</span>}
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          
          <Form.Item name="memo" label={t('memo')}>
            <Input.TextArea 
              rows={3}
              placeholder={t('Optional payment notes or reference')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default withRoleAccess(Accounting, ['superadmin']);